import React, { useEffect, useState } from 'react';
import { Gift, ChevronLeft, Clock, Zap, Star, Award, Crown, Heart, Flame, ChevronRight, Check, Shield, Calendar, Sparkles, X, Diamond } from 'lucide-react';
// Token bundle interface
interface TokenBundle {
  id: string;
  name: string;
  tokens: number;
  bonusTokens: number;
  price: number;
  originalPrice?: number;
  features: string[];
  tag?: string;
  tagColor?: string;
  highlight?: boolean;
  limited?: boolean;
  timeLeft?: number; // in seconds
  mostPopular?: boolean;
  new?: boolean;
  psychologicalTrigger: string;
}
interface TokenStoreProps {
  onBack?: () => void;
  onPurchase?: (bundleId: string) => void;
  currentTokens?: number;
  tokenType?: 'luv' | 'gems';
}
export const TokenStore: React.FC<TokenStoreProps> = ({
  onBack,
  onPurchase,
  currentTokens = 240,
  tokenType = 'luv'
}) => {
  const [selectedBundle, setSelectedBundle] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(3600); // 1 hour in seconds
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [activeTokenType, setActiveTokenType] = useState<'luv' | 'gems'>(tokenType);
  // LuvToken bundles with psychological triggers - now positioned as experience points
  const luvTokenBundles: TokenBundle[] = [{
    id: 'starter',
    name: 'XP Boost Pack',
    tokens: 100,
    bonusTokens: 0,
    price: 1.99,
    features: ['Quick boost for beginners', 'Enhance your experience', 'Instant delivery'],
    tag: 'ENTRY LEVEL',
    tagColor: 'blue',
    psychologicalTrigger: 'Low barrier to entry for new users'
  }, {
    id: 'first_timer',
    name: "Newcomer's Boost",
    tokens: 250,
    bonusTokens: 25,
    price: 3.99,
    features: ['10% bonus XP', 'Priority in chat queues', 'Unlock basic profile features'],
    tag: 'BEST FOR NEW USERS',
    tagColor: 'green',
    new: true,
    psychologicalTrigger: 'Overcoming initial purchase barriers'
  }, {
    id: 'weekly',
    name: 'Weekly Engagement Boost',
    tokens: 500,
    bonusTokens: 50,
    price: 6.99,
    features: ['10% bonus XP', 'Weekly delivery option', 'Save $1 with subscription'],
    tag: 'SUBSCRIPTION',
    tagColor: 'purple',
    psychologicalTrigger: 'Establishing usage habits'
  }, {
    id: 'reengagement',
    name: 'Activity Booster',
    tokens: 150,
    bonusTokens: 30,
    price: 2.49,
    features: ['20% bonus XP', 'Relationship boost', 'Win back attention'],
    tag: 'WELCOME BACK',
    tagColor: 'pink',
    psychologicalTrigger: 'Re-engaging lapsed users'
  }, {
    id: 'valentines',
    name: "Valentine's XP Boost",
    tokens: 300,
    bonusTokens: 60,
    price: 4.99,
    features: ['20% bonus XP', 'Limited time offer', "Special Valentine's perks"],
    tag: 'LIMITED OFFER',
    tagColor: 'red',
    limited: true,
    timeLeft: timeLeft,
    psychologicalTrigger: 'Seasonal scarcity'
  }, {
    id: 'milestone',
    name: 'Milestone XP Boost',
    tokens: 750,
    bonusTokens: 150,
    price: 9.99,
    features: ['20% bonus XP', 'Relationship milestone boost', 'Chat priority features'],
    tag: 'RELATIONSHIP BUILDER',
    tagColor: 'orange',
    psychologicalTrigger: 'Milestone-based rewards'
  }, {
    id: 'jackpot',
    name: 'Lucky XP Boost',
    tokens: 400,
    bonusTokens: 10,
    price: 5.99,
    features: ['Random bonus (10-100 XP)', 'Daily special', 'Excitement guaranteed'],
    tag: 'RANDOM BONUS',
    tagColor: 'yellow',
    psychologicalTrigger: 'Variable reward schedule'
  }, {
    id: 'popular',
    name: 'Popular XP Package',
    tokens: 1000,
    bonusTokens: 250,
    price: 12.99,
    features: ['25% bonus XP', 'VIP chat priority', 'Angel attention boost'],
    tag: 'MOST POPULAR',
    tagColor: 'pink',
    mostPopular: true,
    highlight: true,
    psychologicalTrigger: 'Social proof'
  }, {
    id: 'flash_sale',
    name: 'Flash Sale XP Pack',
    tokens: 400,
    bonusTokens: 100,
    price: 4.99,
    originalPrice: 6.99,
    features: ['25% bonus XP', '28% off regular price', 'Limited time only'],
    tag: 'FLASH SALE',
    tagColor: 'red',
    limited: true,
    timeLeft: timeLeft,
    psychologicalTrigger: 'Urgency and scarcity'
  }, {
    id: 'ultimate',
    name: 'Ultimate XP Package',
    tokens: 2000,
    bonusTokens: 600,
    price: 19.99,
    features: ['30% bonus XP', 'VIP status', 'Premium angel access', 'Priority support'],
    tag: 'BEST VALUE',
    tagColor: 'gold',
    highlight: true,
    psychologicalTrigger: 'Status and exclusivity'
  }];
  // DesireGems bundles - now clearly positioned as premium currency
  const gemTokenBundles: TokenBundle[] = [{
    id: 'gem_starter',
    name: 'Starter Gems Pack',
    tokens: 25,
    bonusTokens: 0,
    price: 9.99,
    features: ['Premium currency for exclusive content', 'Unlock premium photos and videos', 'Instant delivery'],
    tag: 'ENTRY LEVEL',
    tagColor: 'blue',
    psychologicalTrigger: 'Low barrier to entry for premium features'
  }, {
    id: 'gem_basic',
    name: 'Basic Gems Pack',
    tokens: 50,
    bonusTokens: 5,
    price: 19.99,
    features: ['10% bonus gems', 'Unlock premium content', 'VIP chat features'],
    tag: 'POPULAR',
    tagColor: 'purple',
    psychologicalTrigger: 'Good balance of value and affordability'
  }, {
    id: 'gem_premium',
    name: 'Premium Gems Pack',
    tokens: 100,
    bonusTokens: 15,
    price: 39.99,
    features: ['15% bonus gems', 'Unlock exclusive content', 'Premium angel interactions'],
    tag: 'BEST VALUE',
    tagColor: 'pink',
    highlight: true,
    mostPopular: true,
    psychologicalTrigger: 'Best value proposition for committed users'
  }, {
    id: 'gem_flash',
    name: 'Flash Sale Gems',
    tokens: 75,
    bonusTokens: 25,
    price: 24.99,
    originalPrice: 34.99,
    features: ['33% bonus gems', '28% off regular price', 'Limited time only'],
    tag: 'FLASH SALE',
    tagColor: 'red',
    limited: true,
    timeLeft: timeLeft,
    psychologicalTrigger: 'Urgency and scarcity'
  }, {
    id: 'gem_elite',
    name: 'Elite Gems Collection',
    tokens: 250,
    bonusTokens: 50,
    price: 89.99,
    features: ['20% bonus gems', 'Elite status features', 'Exclusive angel access', 'Premium content library'],
    tag: 'ELITE',
    tagColor: 'gold',
    highlight: true,
    psychologicalTrigger: 'Status and exclusivity'
  }];
  // Format time left for countdown timer
  const formatTimeLeft = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor(seconds % 3600 / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  // Get the appropriate token bundles based on active token type
  const getTokenBundles = () => {
    return activeTokenType === 'luv' ? luvTokenBundles : gemTokenBundles;
  };
  // Get the token symbol based on active token type
  const getTokenSymbol = () => {
    return activeTokenType === 'luv' ? '💗' : '💎';
  };
  // Countdown timer for limited time offers
  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft(prevTime => prevTime - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);
  // Handle token type switch
  const handleTokenTypeSwitch = (type: 'luv' | 'gems') => {
    setActiveTokenType(type);
  };
  // Handle bundle selection
  const handleSelectBundle = (bundleId: string) => {
    setSelectedBundle(bundleId);
    setShowConfirmation(true);
  };
  // Handle purchase confirmation
  const handleConfirmPurchase = () => {
    if (selectedBundle && onPurchase) {
      onPurchase(selectedBundle);
    }
    setShowConfirmation(false);
    setSelectedBundle(null);
  };
  // Get selected bundle details
  const getSelectedBundle = () => {
    return getTokenBundles().find(bundle => bundle.id === selectedBundle);
  };
  return <div className="w-full bg-white dark:bg-gray-950 text-gray-900 dark:text-white" data-id="element-21255">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 p-4 flex items-center justify-between" data-id="element-21256">
        <div className="flex items-center gap-2" data-id="element-21257">
          {onBack && <button onClick={onBack} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800" data-id="element-21258">
              <ChevronLeft size={20} className="text-gray-600 dark:text-gray-400" data-id="element-21259" />
            </button>}
          <h1 className="text-xl font-bold" data-id="element-21260">Token Store</h1>
        </div>
        <div className="flex items-center gap-2" data-id="element-21261">
          <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center gap-1.5" data-id="element-21262">
            <div className="text-pink-500 dark:text-pink-400" data-id="element-21263">
              {activeTokenType === 'luv' ? '💗' : '💎'}
            </div>
            <span className="font-medium" data-id="element-21264">{currentTokens}</span>
          </div>
        </div>
      </div>

      {/* Token Type Switcher */}
      <div className="flex items-center justify-center my-4" data-id="element-21265">
        <div className="bg-gray-100 dark:bg-gray-800 rounded-full p-1 flex" data-id="element-21266">
          <button onClick={() => handleTokenTypeSwitch('luv')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${activeTokenType === 'luv' ? 'bg-white dark:bg-gray-700 text-pink-600 dark:text-pink-400 shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-pink-500 dark:hover:text-pink-400'}`} data-id="element-21267">
            <span className="text-pink-500 dark:text-pink-400" data-id="element-21268">💗</span>
            <span data-id="element-21269">LuvTokens (XP)</span>
          </button>
          <button onClick={() => handleTokenTypeSwitch('gems')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${activeTokenType === 'gems' ? 'bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 shadow-sm' : 'text-gray-600 dark:text-gray-300 hover:text-purple-500 dark:hover:text-purple-400'}`} data-id="element-21270">
            <span className="text-purple-500 dark:text-purple-400" data-id="element-21271">💎</span>
            <span data-id="element-21272">DesireGems (Premium)</span>
          </button>
        </div>
      </div>

      {/* Promo Banner */}
      <div className={`p-4 ${activeTokenType === 'luv' ? 'bg-gradient-to-r from-pink-500/10 to-purple-500/10 dark:from-pink-500/20 dark:to-purple-500/20 border-pink-200 dark:border-pink-800/30' : 'bg-gradient-to-r from-purple-500/10 to-blue-500/10 dark:from-purple-500/20 dark:to-blue-500/20 border-purple-200 dark:border-purple-800/30'} 
        m-4 rounded-xl border`} data-id="element-21273">
        <div className="flex items-center gap-4" data-id="element-21274">
          <div className={`w-12 h-12 rounded-full ${activeTokenType === 'luv' ? 'bg-gradient-to-r from-pink-500 to-purple-500' : 'bg-gradient-to-r from-purple-500 to-blue-500'} 
            flex items-center justify-center text-white`} data-id="element-21275">
            {activeTokenType === 'luv' ? <Sparkles size={24} data-id="element-21276" /> : <Diamond size={24} data-id="element-21277" />}
          </div>
          <div className="flex-1" data-id="element-21278">
            <h2 className="font-bold text-lg" data-id="element-21279">
              {activeTokenType === 'luv' ? 'Boost Your Experience!' : 'Premium Content Awaits!'}
            </h2>
            <p className="text-sm" data-id="element-21280">
              {activeTokenType === 'luv' ? 'LuvTokens are primarily earned through activities. Buy XP boosts to level up faster!' : 'DesireGems are premium currency for exclusive content. Unlock the best experiences!'}
            </p>
          </div>
        </div>
      </div>

      {/* Additional Info Banner for LuvTokens */}
      {activeTokenType === 'luv' && <div className="mx-4 mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800/30 text-sm" data-id="element-21281">
          <div className="flex items-start gap-2" data-id="element-21282">
            <div className="text-blue-500 dark:text-blue-400 mt-0.5" data-id="element-21283">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-21284">
                <circle cx="12" cy="12" r="10" data-id="element-21285"></circle>
                <line x1="12" y1="16" x2="12" y2="12" data-id="element-21286"></line>
                <line x1="12" y1="8" x2="12.01" y2="8" data-id="element-21287"></line>
              </svg>
            </div>
            <div data-id="element-21288">
              <p className="text-blue-800 dark:text-blue-300" data-id="element-21289">
                <strong data-id="element-21290">Did you know?</strong> LuvTokens are mostly earned
                through regular activities:
              </p>
              <ul className="mt-1 list-disc list-inside text-blue-700 dark:text-blue-400" data-id="element-21291">
                <li data-id="element-21292">Daily logins (50 LuvTokens)</li>
                <li data-id="element-21293">Engaging with angels (10-25 LuvTokens per interaction)</li>
                <li data-id="element-21294">Completing challenges (25-100 LuvTokens)</li>
                <li data-id="element-21295">Relationship milestones (50-200 LuvTokens)</li>
              </ul>
            </div>
          </div>
        </div>}

      {/* Token Bundles */}
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-21296">
        {getTokenBundles().map(bundle => <div key={bundle.id} className={`border rounded-xl overflow-hidden transition-all duration-300 relative ${bundle.highlight ? activeTokenType === 'luv' ? 'border-pink-500 dark:border-pink-500 shadow-md shadow-pink-500/10' : 'border-purple-500 dark:border-purple-500 shadow-md shadow-purple-500/10' : 'border-gray-200 dark:border-gray-800 hover:border-pink-300 dark:hover:border-pink-800'}`} data-id="element-21297">
            {/* Bundle Tag */}
            {bundle.tag && <div className={`absolute top-0 left-0 px-2 py-1 text-xs font-bold text-white 
                ${bundle.tagColor === 'pink' ? 'bg-pink-500' : bundle.tagColor === 'blue' ? 'bg-blue-500' : bundle.tagColor === 'green' ? 'bg-green-500' : bundle.tagColor === 'purple' ? 'bg-purple-500' : bundle.tagColor === 'red' ? 'bg-red-500' : bundle.tagColor === 'orange' ? 'bg-orange-500' : bundle.tagColor === 'yellow' ? 'bg-yellow-500' : bundle.tagColor === 'gold' ? 'bg-gradient-to-r from-yellow-500 to-amber-500' : 'bg-gray-500'}`} data-id="element-21298">
                {bundle.tag}
              </div>}
            {/* Most Popular Badge */}
            {bundle.mostPopular && <div className="absolute top-0 right-0 px-2 py-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs font-bold" data-id="element-21299">
                MOST POPULAR
              </div>}
            {/* New Badge */}
            {bundle.new && <div className="absolute top-0 right-0 px-2 py-1 bg-green-500 text-white text-xs font-bold" data-id="element-21300">
                NEW
              </div>}
            <div className="p-4" data-id="element-21301">
              <div className="flex justify-between items-start mb-2" data-id="element-21302">
                <h3 className="font-bold text-lg" data-id="element-21303">{bundle.name}</h3>
                {bundle.originalPrice && <div className="flex flex-col items-end" data-id="element-21304">
                    <span className="text-xs text-gray-500 dark:text-gray-400 line-through" data-id="element-21305">
                      ${bundle.originalPrice.toFixed(2)}
                    </span>
                    <span className="text-lg font-bold text-pink-500 dark:text-pink-400" data-id="element-21306">
                      ${bundle.price.toFixed(2)}
                    </span>
                  </div>}
                {!bundle.originalPrice && <span className="text-lg font-bold" data-id="element-21307">
                    ${bundle.price.toFixed(2)}
                  </span>}
              </div>
              <div className="flex items-center gap-2 mb-3" data-id="element-21308">
                <div className="text-2xl" data-id="element-21309">{getTokenSymbol()}</div>
                <div data-id="element-21310">
                  <div className="font-bold text-xl" data-id="element-21311">
                    {bundle.tokens.toLocaleString()}
                  </div>
                  {bundle.bonusTokens > 0 && <div className="text-sm text-green-600 dark:text-green-400" data-id="element-21312">
                      +{bundle.bonusTokens.toLocaleString()} bonus
                    </div>}
                </div>
              </div>
              {/* Limited Time Countdown */}
              {bundle.limited && bundle.timeLeft && <div className="mb-3 p-2 bg-red-500/10 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-900/30" data-id="element-21313">
                  <div className="flex items-center gap-2 text-red-600 dark:text-red-400" data-id="element-21314">
                    <Clock size={16} data-id="element-21315" />
                    <span className="text-sm font-medium" data-id="element-21316">
                      Ends in: {formatTimeLeft(bundle.timeLeft)}
                    </span>
                  </div>
                </div>}
              {/* Features */}
              <ul className="mb-4 space-y-1" data-id="element-21317">
                {bundle.features.map((feature, index) => <li key={index} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300" data-id="element-21318">
                    <Check size={16} className="text-green-500" data-id="element-21319" />
                    <span data-id="element-21320">{feature}</span>
                  </li>)}
              </ul>
              {/* Psychological Trigger - Hidden in UI but influencing design */}
              {/* <div className="text-xs text-gray-500 mb-3">{bundle.psychologicalTrigger}</div> */}
              <button onClick={() => handleSelectBundle(bundle.id)} className={`w-full py-2 rounded-lg font-medium ${bundle.highlight ? activeTokenType === 'luv' ? 'bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white' : 'bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-white'}`} data-id="element-21321">
                Get {activeTokenType === 'luv' ? 'Tokens' : 'Gems'}
              </button>
            </div>
          </div>)}
      </div>

      {/* Value Comparison Button */}
      <div className="p-4 flex justify-center" data-id="element-21322">
        <button onClick={() => setShowComparisonModal(true)} className={`flex items-center gap-2 ${activeTokenType === 'luv' ? 'text-pink-500 dark:text-pink-400' : 'text-purple-500 dark:text-purple-400'} font-medium`} data-id="element-21323">
          <Zap size={16} data-id="element-21324" />
          <span data-id="element-21325">Compare Value</span>
          <ChevronRight size={16} data-id="element-21326" />
        </button>
      </div>

      {/* Subscription Info */}
      <div className="p-4 m-4 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800" data-id="element-21327">
        <div className="flex items-center gap-3" data-id="element-21328">
          <div className={`w-10 h-10 rounded-full ${activeTokenType === 'luv' ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400' : 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'} flex items-center justify-center`} data-id="element-21329">
            <Calendar size={20} data-id="element-21330" />
          </div>
          <div data-id="element-21331">
            <h3 className="font-bold" data-id="element-21332">
              {activeTokenType === 'luv' ? 'Weekly Token Subscription' : 'Premium Gems Subscription'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-21333">
              {activeTokenType === 'luv' ? 'Save up to 20% with our weekly token subscription plans' : 'Get premium gems delivered monthly with 25% bonus'}
            </p>
          </div>
        </div>
        <button className={`mt-3 w-full py-2 ${activeTokenType === 'luv' ? 'bg-pink-600 hover:bg-pink-700' : 'bg-purple-600 hover:bg-purple-700'} text-white rounded-lg font-medium`} onClick={() => {
        // Navigate to the membership/subscription section
        const event = new CustomEvent('navigate', {
          detail: {
            section: 'subscription'
          }
        });
        document.dispatchEvent(event);
      }} data-id="element-21334">
          View Subscription Plans
        </button>
      </div>

      {/* Purchase Security */}
      <div className="p-4 flex items-center justify-center gap-3 text-sm text-gray-600 dark:text-gray-400 border-t border-gray-200 dark:border-gray-800" data-id="element-21335">
        <div className="flex items-center gap-1" data-id="element-21336">
          <Shield size={14} data-id="element-21337" />
          <span data-id="element-21338">Secure Payment</span>
        </div>
        <div className="flex items-center gap-1" data-id="element-21339">
          <Zap size={14} data-id="element-21340" />
          <span data-id="element-21341">Instant Delivery</span>
        </div>
        <div className="flex items-center gap-1" data-id="element-21342">
          <Shield size={14} data-id="element-21343" />
          <span data-id="element-21344">Privacy Protected</span>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-21345">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full p-6 animate-in zoom-in-95 duration-150" data-id="element-21346">
            <div className="flex justify-between items-start mb-4" data-id="element-21347">
              <h3 className="text-xl font-bold" data-id="element-21348">Confirm Purchase</h3>
              <button onClick={() => setShowConfirmation(false)} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800" data-id="element-21349">
                <X size={20} className="text-gray-500 dark:text-gray-400" data-id="element-21350" />
              </button>
            </div>
            {getSelectedBundle() && <>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4" data-id="element-21351">
                  <div className="flex justify-between items-center mb-2" data-id="element-21352">
                    <span className="font-medium" data-id="element-21353">
                      {getSelectedBundle()?.name}
                    </span>
                    <span className="font-bold" data-id="element-21354">
                      ${getSelectedBundle()?.price.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-21355">
                    <span className="text-xl" data-id="element-21356">{getTokenSymbol()}</span>
                    <div data-id="element-21357">
                      <span className="font-bold" data-id="element-21358">
                        {getSelectedBundle()?.tokens.toLocaleString()}
                      </span>
                      {getSelectedBundle()?.bonusTokens ? <span className="text-sm text-green-600 dark:text-green-400" data-id="element-21359">
                          {' '}
                          +{getSelectedBundle()?.bonusTokens.toLocaleString()}{' '}
                          bonus
                        </span> : null}
                    </div>
                  </div>
                </div>
                <div className="space-y-3 mb-4" data-id="element-21360">
                  <div className="flex justify-between" data-id="element-21361">
                    <span className="text-gray-600 dark:text-gray-400" data-id="element-21362">
                      Subtotal
                    </span>
                    <span data-id="element-21363">${getSelectedBundle()?.price.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between" data-id="element-21364">
                    <span className="text-gray-600 dark:text-gray-400" data-id="element-21365">
                      Taxes
                    </span>
                    <span data-id="element-21366">$0.00</span>
                  </div>
                  <div className="flex justify-between font-bold pt-2 border-t border-gray-200 dark:border-gray-700" data-id="element-21367">
                    <span data-id="element-21368">Total</span>
                    <span data-id="element-21369">${getSelectedBundle()?.price.toFixed(2)}</span>
                  </div>
                </div>
                <div className="flex gap-3" data-id="element-21370">
                  <button onClick={() => setShowConfirmation(false)} className="flex-1 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg font-medium" data-id="element-21371">
                    Cancel
                  </button>
                  <button onClick={handleConfirmPurchase} className={`flex-1 py-2 ${activeTokenType === 'luv' ? 'bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600' : 'bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600'} text-white rounded-lg font-medium`} data-id="element-21372">
                    Confirm Purchase
                  </button>
                </div>
              </>}
          </div>
        </div>}

      {/* Value Comparison Modal */}
      {showComparisonModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-id="element-21373">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-2xl w-full p-6 animate-in zoom-in-95 duration-150" data-id="element-21374">
            <div className="flex justify-between items-start mb-4" data-id="element-21375">
              <h3 className="text-xl font-bold" data-id="element-21376">
                {activeTokenType === 'luv' ? 'LuvToken' : 'DesireGem'} Bundle
                Value Comparison
              </h3>
              <button onClick={() => setShowComparisonModal(false)} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800" data-id="element-21377">
                <X size={20} className="text-gray-500 dark:text-gray-400" data-id="element-21378" />
              </button>
            </div>
            <div className="overflow-x-auto" data-id="element-21379">
              <table className="w-full" data-id="element-21380">
                <thead data-id="element-21381">
                  <tr className="border-b border-gray-200 dark:border-gray-800" data-id="element-21382">
                    <th className="py-2 text-left text-gray-600 dark:text-gray-400 font-medium" data-id="element-21383">
                      Bundle
                    </th>
                    <th className="py-2 text-right text-gray-600 dark:text-gray-400 font-medium" data-id="element-21384">
                      Price
                    </th>
                    <th className="py-2 text-right text-gray-600 dark:text-gray-400 font-medium" data-id="element-21385">
                      {activeTokenType === 'luv' ? 'Tokens' : 'Gems'}
                    </th>
                    <th className="py-2 text-right text-gray-600 dark:text-gray-400 font-medium" data-id="element-21386">
                      Bonus
                    </th>
                    <th className="py-2 text-right text-gray-600 dark:text-gray-400 font-medium" data-id="element-21387">
                      Total
                    </th>
                    <th className="py-2 text-right text-gray-600 dark:text-gray-400 font-medium" data-id="element-21388">
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-21389">
                  {getTokenBundles().map(bundle => {
                const totalTokens = bundle.tokens + bundle.bonusTokens;
                const valuePerDollar = totalTokens / bundle.price;
                return <tr key={bundle.id} className="border-b border-gray-200 dark:border-gray-800" data-id="element-21390">
                        <td className="py-3 text-left" data-id="element-21391">
                          <div className="font-medium" data-id="element-21392">{bundle.name}</div>
                          {bundle.mostPopular && <span className={`text-xs ${activeTokenType === 'luv' ? 'text-pink-500 dark:text-pink-400' : 'text-purple-500 dark:text-purple-400'}`} data-id="element-21393">
                              Most Popular
                            </span>}
                        </td>
                        <td className="py-3 text-right" data-id="element-21394">
                          ${bundle.price.toFixed(2)}
                        </td>
                        <td className="py-3 text-right" data-id="element-21395">
                          {bundle.tokens.toLocaleString()}
                        </td>
                        <td className="py-3 text-right text-green-600 dark:text-green-400" data-id="element-21396">
                          {bundle.bonusTokens > 0 ? `+${bundle.bonusTokens.toLocaleString()}` : '-'}
                        </td>
                        <td className="py-3 text-right font-bold" data-id="element-21397">
                          {totalTokens.toLocaleString()}
                        </td>
                        <td className="py-3 text-right" data-id="element-21398">
                          <div className="flex items-center justify-end gap-1" data-id="element-21399">
                            <span data-id="element-21400">{valuePerDollar.toFixed(1)}</span>
                            <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-21401">
                              {activeTokenType === 'luv' ? 'tokens' : 'gems'}/$
                            </span>
                          </div>
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
            <div className="mt-6 text-center" data-id="element-21402">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" data-id="element-21403">
                The higher the "Value" number, the more{' '}
                {activeTokenType === 'luv' ? 'tokens' : 'gems'} you get per
                dollar spent.
              </p>
              <button onClick={() => setShowComparisonModal(false)} className={`px-6 py-2 ${activeTokenType === 'luv' ? 'bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600' : 'bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600'} text-white rounded-lg font-medium`} data-id="element-21404">
                Close Comparison
              </button>
            </div>
          </div>
        </div>}
    </div>;
};